import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Lot, Reserve} from "../common/types/Types";
import {Link, useHistory, useLocation} from "react-router-dom";
import Loader from "../common/views/Loader";
import {useFormik} from "formik";
import {getInitialReserveValues, getReserveSchema} from "../common/types/Schemas";
import {useAlert} from 'react-alert';
import {BYInput} from "../common/inputs/BYInput";
import {EntitySelect} from "../common/inputs/EntitySelect";
import PlanView from "./PlanView";
import {useLot, useReserve, useUpdateReserve} from '../api/hooks';
import ReserveBadge from "./ReserveBadge";
import BYSwitch from "../common/inputs/BYSwitch";
import {AuthContext} from "../providers/AuthProvider";
import Footer from "../common/views/Footer";
import {Animated} from "react-animated-css";
import {CommonHelper} from "../common/CommonHelper";
import BYDatePicker from "../common/inputs/BYDatePicker";
import BYStatusButtonGroup from "../common/inputs/BYStatusButtonGroup";

interface ReserveIndexProps {
    match: {
        params: {
            id: string;
            idLot: string;
            idOperation: string;
            idCleTransverse: string;
            idSalesforce: string;
        }
    },
    copy?: boolean;
}

const loadFile = (file): Promise<FileReader> => {
    return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => {
            resolve(fr);
        };
        fr.onerror = (err) => {
            reject(err);
        };
        fr.readAsDataURL(file);
    });
};

const ReserveIndex = (props: ReserveIndexProps) => {
    const history = useHistory();
    const {search, pathname} = useLocation();
    const alert = useAlert();
    const photoInputRef = useRef<HTMLInputElement>(null);
    const docInputRef = useRef<HTMLInputElement>(null);
    const [loadedPhoto, setLoadedPhoto] = useState<string>();
    const [loadedDoc, setLoadedDoc] = useState<string>();
    const {copy = false} = props;

    const queryParams = useMemo(() => new URLSearchParams(search), [search]);

    const reserveId = props.match.params.id;
    const idCleTransverse = props.match.params.idCleTransverse;
    const idSalesforce = props.match.params.idSalesforce;
    const [lotId, setLotId] = useState(Number(props.match.params.idLot));
    const operationId = Number(props.match.params.idOperation);
    const isNew = props.match.params.id === 'new' || props.match.params.id === undefined;
    const {data: reserve = {} as Reserve, isLoading} = useReserve(reserveId);
    const lotRef = idCleTransverse ? idCleTransverse : reserve.lotRefLot || lotId;
    const isCleTransverse = !!idCleTransverse;

    const {data: lot = {} as Lot} = useLot(lotRef, isCleTransverse);
    const {mutateAsync: mutateReserve} = useUpdateReserve();

    const {getOperation} = useContext(AuthContext);

    //Can edit phase if selected phase is 'Pré-livraison', 'OPR' or 'Visite cloison'
    // const canEditPhase = [6, 7, 8].includes(reserve.phaseRefPhase) || isNew;
    const canEditPhase = true;

    const operation = getOperation(lot.operationRefOperation || operationId);

    useEffect(() => {
        if (reserve.photos?.length) {
            setLoadedPhoto(CommonHelper.getRemoteReservePhotoUrl(reserve));
        }
    }, [reserve]);

    const formik = useFormik({
        initialValues: getInitialReserveValues(lotId),
        validationSchema: getReserveSchema(isNew),
        onSubmit: async (values) => {
            try {
                const updateReserve = reserveId && !isNew && !copy && !values.copy;
                const photoFile = loadedPhoto && photoInputRef.current?.files?.[0];
                const docFile = loadedDoc && docInputRef.current?.files?.[0];

                const newReserve = await mutateReserve({
                    values,
                    reserveId,
                    updateReserve,
                    photoFile,
                    docFile,
                    operationId: operation.refOperation
                });

                alert.info(<><Link to={`/reserve/${newReserve.refReserve}`}>Reserve
                    #{newReserve.numBadge}</Link> sauvegardée</>);

                const fromOperation = pathname.includes('operation');

                if (values.copy) {
                    history.push(`/reserve/${newReserve.refReserve}/copy`);
                } else if (fromOperation) {
                    history.push(`/operation/${lot.operationRefOperation || operationId}`);
                } else {
                    history.push(`/operation/${lot.operationRefOperation || operationId}/lot/${lot.refLot || lotId}`);
                }

            } catch (error) {
                const statusCode = error.response && error.response.status;

                if (statusCode === 401) {
                    alert.error('Valeurs invalides ou incomplètes');
                } else if (statusCode === 409) {
                    alert.error(error.response.data);
                } else {
                    alert.error(error.message);
                }
            }
        }
    });

    useEffect(() => {
        const salesforceId = queryParams.get('salesforce_id') || idSalesforce;
        if (salesforceId) {
            formik.setValues({
                ...formik.values,
                numeroDemandeSalesforce: salesforceId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams, idSalesforce])

    useEffect(() => {

        if (!isNew && !isLoading) {
            formik.setValues({
                lotRefLot: reserve.lotRefLot,
                libelle: reserve.libelle,
                libelleMoe: reserve.libelleMoe,
                indicSupprime: reserve.indicSupprime,
                commentaire: reserve.commentaire,
                urlDocLevee: reserve.urlDocLevee,
                coordonneeX: reserve.coordonneeX,
                coordonneeY: reserve.coordonneeY,
                planRefPlan: reserve.planRefPlan,
                corpsEtatEntrepriseRefCorpsEtatEntreprise: reserve.corpsEtatEntrepriseRefCorpsEtatEntreprise,
                phaseRefPhase: reserve.phaseRefPhase,
                statutRefStatut: reserve.statutRefStatut,
                aPourLocalisations: reserve.aPourLocalisations.map(aPl => aPl.localisation.refLocalisation),
                aPourSituations: reserve.aPourSituations.map(aPs => aPs.situation.refSituation),
                dateEcheance: reserve.dateEcheance,
                dateLevee: reserve.dateLevee,
                numeroDemandeSalesforce: reserve.numeroDemandeSalesforce,
                copy: false,
            });

            setLotId(reserve.lotRefLot);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reserve, isNew, isLoading]);

    useEffect(() => {
        if (isNew && lot.plans) {
            const lastPlan = lot.plans[lot.plans.length - 1];
            formik.setValues({
                ...formik.values,
                planRefPlan: lastPlan.refPlan
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNew, lot]);

    if (isLoading) {
        return <div className="text-center my-6"><Loader/></div>;
    }

    return <Animated animationIn="fadeIn" animationOut="fadeOut" isVisible={true}>
        <div className="container">
            <form onSubmit={(e) => {
                e.preventDefault();
                formik.handleSubmit(e);
            }} autoComplete="off">

                <div className="row py-4">
                    <div className="col-8">

                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item" aria-current="page">
                                    {operation?.refOperation ? <Link
                                            to={`/operation/${operation?.refOperation}`}>{operation?.nomOperation}</Link> :
                                        <a>Opération</a>}</li>
                                <li className="breadcrumb-item" aria-current="page">
                                    {!!lot?.refLot && operation?.refOperation ?
                                        <Link
                                            to={`/operation/${operation?.refOperation}/lot/${lot?.refLot}`}>Lot {lot.numeroLot}</Link> :
                                        <a>Lot</a>}
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    <h4>
                                        {!(isNew || copy) ? <ReserveBadge reserve={reserve}/> : null}
                                        {isNew || copy ? 'Création de réserve' : ' Modification de réserve'}</h4>
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>

                <div className="input-container">

                    {!isNew && !copy && <div className="mb-2">
                        {reserve.saisiPar && <>Créée par&nbsp;
                            <Link
                                to={`/user/${reserve.saisiPar.refUti}`}>{CommonHelper.formatAcquereur(reserve.saisiPar)}</Link>
                            &nbsp;le {CommonHelper.formatDate(reserve.dateSaisi)}
                        </>}

                        {reserve.modifPar && <>&nbsp;&nbsp;-&nbsp;&nbsp;Modifiée par&nbsp;
                            <Link
                                to={`/user/${reserve.modifPar.refUti}`}>{CommonHelper.formatAcquereur(reserve.modifPar)}</Link>
                            &nbsp;le {CommonHelper.formatDate(reserve.dateModif)}
                        </>}

                        {reserve.supprimePar && <>&nbsp;&nbsp;-&nbsp;&nbsp;Supprimée par&nbsp;
                            <Link
                                to={`/user/${reserve.supprimePar.refUti}`}>{CommonHelper.formatAcquereur(reserve.supprimePar)}</Link>
                            &nbsp;le {CommonHelper.formatDate(reserve.dateSuppression)}
                        </>}
                    </div>}

                    <div className="row mb-3">
                        <div className="col">

                            <EntitySelect
                                type="lot"
                                title="Lot / Client"
                                value={formik.values.lotRefLot}
                                onBlur={formik.handleBlur}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        lotRefLot: newValue
                                    });
                                    setLotId(newValue);
                                }}
                                disabled={!isNew}
                                required={true}
                                operationId={operation?.refOperation}
                                touched={formik.touched.lotRefLot}
                                error={formik.errors.lotRefLot}
                            />
                        </div>
                        <div className="col">
                            <EntitySelect
                                type="enterprise"
                                title="Entreprise / Corps d'État"
                                value={formik.values.corpsEtatEntrepriseRefCorpsEtatEntreprise}
                                onBlur={formik.handleBlur}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        corpsEtatEntrepriseRefCorpsEtatEntreprise: newValue
                                    })
                                }}
                                required={true}
                                operationId={operation?.refOperation}
                                touched={formik.touched.corpsEtatEntrepriseRefCorpsEtatEntreprise}
                                error={formik.errors.corpsEtatEntrepriseRefCorpsEtatEntreprise}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-6">
                            <EntitySelect
                                type="phase"
                                title="Phase *"
                                value={formik.values.phaseRefPhase}
                                onBlur={formik.handleBlur}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        phaseRefPhase: newValue
                                    })
                                }}
                                disabled={!canEditPhase}
                                required={true}
                                touched={formik.touched.phaseRefPhase}
                                error={formik.errors.phaseRefPhase}
                            />
                        </div>
                        <div className="col-6">
                            {!reserve.indicRas &&
                                <BYStatusButtonGroup
                                    title="Statut *"
                                    value={formik.values.statutRefStatut}
                                    onChange={(newValue) => {
                                        formik.setValues({
                                            ...formik.values,
                                            statutRefStatut: newValue,
                                            ...(newValue === 2 ? { dateLevee: formik.values.dateLevee || new Date()} : undefined)
                                        })
                                    }}
                                    disabled={isNew}
                                    required={true}
                                />}
                        </div>
                    </div>

                    {formik.values.statutRefStatut === 2 && <div className="row mb-3">
                        <div className="col">
                            <BYDatePicker
                                name="dateLevee"
                                title=" Date Levée Réserve Entreprise"
                                value={formik.values.dateLevee}
                                onChange={(newValue: Date) => {
                                    formik.setValues({
                                        ...formik.values,
                                        dateLevee: newValue?.toISOString()
                                    })
                                }}
                                required={true}
                                touched={formik.touched.dateLevee}
                                error={formik.errors.dateLevee}
                            />
                        </div>
                        <div className="col-6">
                                <label htmlFor="exampleFormControlFile1">Quitus Réserve Entreprise</label>
                                {!formik.values.urlDocLevee && <input ref={docInputRef}
                                                                      type="file"
                                                                      className="form-control-file"
                                                                      accept="application/pdf,application/msword"
                                                                      onChange={(e) => {
                                                                          if (e.target.files && e.target.files.length) {
                                                                              loadFile(e.target.files[0]).then((fr) => {
                                                                                  setLoadedDoc(fr.result as string);
                                                                              });
                                                                          }
                                                                      }}
                                />}
                                {formik.values.urlDocLevee && <div>
                                    <a href={formik.values.urlDocLevee} target="_blank" rel="noreferrer"
                                       className="btn btn-secondary">
                                        <i className="fa fa-download"></i>
                                    </a>

                                    <button type="button" className="btn btn-danger"
                                            onClick={() => {
                                                setLoadedDoc(undefined);
                                                formik.setValues({
                                                    ...formik.values,
                                                    urlDocLevee: '',
                                                })
                                            }}>
                                        <i className="fa fa-times"></i>
                                    </button>
                                </div>}
                        </div>
                    </div>}

                    <div className="row mb-3">
                        <div className="col">
                            <EntitySelect
                                type="localisation"
                                lotId={lotId}
                                phaseId={formik.values.phaseRefPhase}
                                isMulti={true}
                                maxItemsSelection={5}
                                title="Localisations *"
                                value={formik.values.aPourLocalisations}
                                onBlur={formik.handleBlur}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        aPourLocalisations: newValue
                                    })
                                }}
                                required={true}
                                touched={formik.touched.aPourLocalisations}
                                error={formik.errors.aPourLocalisations}
                            />
                        </div>
                        <div className="col">
                            <EntitySelect
                                type="situation"
                                isMulti={true}
                                maxItemsSelection={5}
                                title="Situations"
                                placeholder="Situations"
                                value={formik.values.aPourSituations}
                                onBlur={formik.handleBlur}
                                onChange={(newValue) => {
                                    formik.setValues({
                                        ...formik.values,
                                        aPourSituations: newValue
                                    })
                                }}
                                required={true}
                                touched={formik.touched.aPourSituations}
                                error={formik.errors.aPourSituations}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col">
                            <PlanView plan={reserve.plan} reserves={[reserve]}/>
                        </div>
                        <div className="col">
                            <h5 className="color-blue">Photo</h5>
                            <div className="text-center">
                                {loadedPhoto &&
                                    <img className="img-plan-200"
                                         src={loadedPhoto}
                                         alt={`Réserve #${reserve.numBadge}`}
                                    />}

                                {loadedPhoto &&
                                    <button type="button" className="btn btn-danger btn-delete-photo"
                                            onClick={() => setLoadedPhoto(undefined)}>
                                        <i className="fa fa-times"></i>
                                    </button>}
                            </div>

                            <div className="form-group d-none">
                                <label htmlFor="exampleFormControlFile1">Example file input</label>
                                <input ref={photoInputRef}
                                       type="file"
                                       className="form-control-file"
                                       accept="image/x-png,image/gif,image/jpeg"
                                       onChange={(e) => {
                                           if (e.target.files && e.target.files.length) {
                                               loadFile(e.target.files[0]).then((fr) => {
                                                   setLoadedPhoto(fr.result as string);
                                               });
                                           }
                                       }}/>
                            </div>

                            {!loadedPhoto && <div className="add-photo-container">
                                <button type="button" className="btn btn-secondary"
                                        onClick={() => photoInputRef.current.click()}>
                                    <i className="fa fa-camera fa-2x"></i>
                                </button>
                            </div>}
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col">
                            <BYInput
                                type="textarea"
                                name="commentaire"
                                placeholder="Commentaire interne"
                                title={formik.values.commentaire?.length !== undefined 
                                ? `Commentaire interne de suivi (500 caractères max.) ${formik.values.commentaire?.length}` 
                                : "Commentaire interne de suivi (500 caractères max.)"}
                                className="form-control"
                                value={formik.values.commentaire}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                touched={formik.touched.commentaire}
                                error={formik.errors.commentaire}
                            />
                        </div>
                        <div className="col">
                            <BYInput
                                type="text"
                                name="libelle"
                                title="Libellé *"
                                className="form-control"
                                value={formik.values.libelle}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                touched={formik.touched.libelle}
                                error={formik.errors.libelle}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col">
                            <BYInput
                                type="text"
                                name="numeroDemandeSalesforce"
                                title="Numéro de demande Salesforce"
                                className="form-control"
                                autoComplete="off"
                                value={formik.values.numeroDemandeSalesforce}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                required={false}
                                touched={formik.touched.numeroDemandeSalesforce}
                                error={formik.errors.numeroDemandeSalesforce}
                                disabled={true}
                            />
                        </div>
                        <div className="col">
                            <BYDatePicker
                                name="dateEcheance"
                                title="Date d'échéance"
                                value={formik.values.dateEcheance}
                                onChange={(newValue: Date) => {
                                    formik.setValues({
                                        ...formik.values,
                                        dateEcheance: newValue?.toISOString()
                                    })
                                }}
                                required={false}
                                touched={formik.touched.dateEcheance}
                                error={formik.errors.dateEcheance}
                            />
                        </div>
                    </div>

                </div>

                <div className="row my-5">
                    <div className="col-7  d-flex">
                        <button className="btn btn-secondary flex-grow-1" type="submit"
                                name="save"
                                disabled={formik.isSubmitting}>
                            Sauvegarder{' '}
                            {!formik.isSubmitting && <i className="fa fa-save"/>}
                            {formik.isSubmitting && <div className="spinner-grow spinner-grow-sm" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>}
                        </button>
                    </div>
                    <div className="col-3 d-flex">
                        <button className="btn btn-primary flex-grow-1"
                                type="button"
                                name="next"
                                onClick={() => {
                                    formik.setFieldValue('copy', true);
                                    formik.handleSubmit();
                                }}
                                disabled={formik.isSubmitting}>
                            Dupliquer / Scinder{' '}
                            {!formik.isSubmitting && <><i className="fa fa-save"/> <i
                                className="fa fa-arrow-right"/></>}
                            {formik.isSubmitting && <div className="spinner-grow spinner-grow-sm" role="status">
                                <span className="sr-only">Loading...</span>
                            </div>}
                        </button>
                    </div>

                    <div className="col-2 delete-button">
                        {!isNew && <BYSwitch
                            onChange={(checked) => {
                                formik.setValues({
                                    ...formik.values,
                                    indicSupprime: checked
                                })
                            }}
                            themeColor="red"
                            disabled={reserve.indicSupprime}
                            checked={formik.values.indicSupprime}
                            title={'Supprimée'}
                            touched={formik.touched.indicSupprime}
                            error={formik.errors.indicSupprime}
                        />}
                    </div>
                </div>
            </form>
        </div>
        <Footer/>

    </Animated>
};

export default ReserveIndex;
